import { useEffect, useState, useCallback } from "react";
import { Pencil, MapPin } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import { TextField, TextareaField, FormFooterActions } from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";

const EMPTY = { id: "", slug: "", name: "", description: "", image_url: "", projects_completed: 0 };

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);

export default function AdminAreas() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/service-areas").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (item) => setEditing(item ? { ...item } : { ...EMPTY });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.name || !editing.image_url) {
      toast.error("Name and Image are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, slug: editing.slug || slugify(editing.name) };
      await api.post("/admin/service-areas", payload);
      toast.success("Area saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return close();
    if (!window.confirm(`Delete "${editing.name}"?`)) return;
    try {
      await api.delete(`/admin/service-areas/${editing.id}`);
      toast.success("Area deleted");
      close();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-areas">
      <AdminHeader
        title="Service Areas"
        subtitle="Cities you serve. Each area has its own SEO landing page at /areas/{slug}."
        action="Add Area"
        actionTestId="add-area-btn"
        onAction={() => open(null)}
        count={items.length}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a) => (
          <button
            key={a.id}
            data-testid={`area-row-${a.id}`}
            onClick={() => open(a)}
            className="group text-left bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors overflow-hidden"
          >
            <div className="aspect-[16/10] overflow-hidden bg-[#0d0d0d]">
              {a.image_url && (
                <img src={a.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${a.image_url}` : a.image_url} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="font-serif-display text-xl">{a.name}</div>
                <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF]" />
              </div>
              <div className="text-xs text-[#D8D6D0]/50 mt-1 flex items-center gap-2">
                <MapPin className="w-3 h-3" /> /areas/{a.slug}
              </div>
              <p className="text-xs text-[#D8D6D0]/55 mt-3 line-clamp-2">{a.description}</p>
              <div className="text-xs text-[#8C1D30] uppercase tracking-widest mt-3">{a.projects_completed}+ projects</div>
            </div>
          </button>
        ))}
      </div>

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit Area" : "New Service Area"}
        subtitle={editing?.id ? `Slug: ${editing.slug}` : "Add a new city to your service map"}
        footer={
          <FormFooterActions
            onCancel={close}
            onSubmit={save}
            onDelete={editing?.id ? remove : null}
            submitting={saving}
          />
        }
      >
        {editing && (
          <>
            <TextField id="name" label="City Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v, slug: editing.slug || slugify(v) })} required testId="area-name" placeholder="Edmonton" />
            <TextField id="slug" label="URL Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: slugify(v) })} hint="Used in URL: /areas/{slug}" />
            <TextareaField id="description" label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={4} required />
            <TextField id="projects_completed" label="Projects Completed" value={editing.projects_completed} onChange={(v) => setEditing({ ...editing, projects_completed: v || 0 })} type="number" />
            <ImageUpload label="Hero Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} required testId="area-image" />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
